/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1744761600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Apr 16 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "crowdtwist",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to CrowdTwist",
          "depth": 0,
          "id": "connect-to-crowdtwist"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API documentation"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Users"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to CrowdTwist as users"
            }), _jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.oracle.com/en/cloud/saas/marketing/crowdtwist-develop/Developers/UserUpdate.html",
                children: "User Update"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-crowdtwist",
        children: "Connect to CrowdTwist"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "CrowdTwist"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Enter your API key. If you do not have a CrowdTwist API key, follow the instructions ", _jsx(_components.a, {
          href: "https://docs.oracle.com/en/cloud/saas/marketing/crowdtwist-develop/Developers/GeneratingAPIKeys.htm",
          children: "here on CrowdTwist"
        }), " to generate a Client API key."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your CrowdTwist destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the CrowdTwist destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "CrowdTwist uses the following ID types to identify users:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email address"
        }), "\n", _jsx(_components.li, {
          children: "Meta user ID"
        }), "\n", _jsx(_components.li, {
          children: "X user ID"
        }), "\n", _jsx(_components.li, {
          children: "CrowdTwist ID"
        }), "\n", _jsx(_components.li, {
          children: "Third party ID"
        }), "\n", _jsx(_components.li, {
          children: "Username"
        }), "\n", _jsx(_components.li, {
          children: "Mobile phone number"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Select which ID you'd like to use to identify users in your model."
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Sync any column in your model to map to any of the following user attributes in CrowdTwist:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "First name"
        }), "\n", _jsx(_components.li, {
          children: "Last name"
        }), "\n", _jsx(_components.li, {
          children: "Date of birth"
        }), "\n", _jsx(_components.li, {
          children: "Email address"
        }), "\n", _jsx(_components.li, {
          children: "Email is verified"
        }), "\n", _jsx(_components.li, {
          children: "Is active"
        }), "\n", _jsx(_components.li, {
          children: "Remove points"
        }), "\n", _jsx(_components.li, {
          children: "Language preference"
        }), "\n", _jsx(_components.li, {
          children: "Postal code"
        }), "\n", _jsx(_components.li, {
          children: "Username"
        }), "\n", _jsx(_components.li, {
          children: "Password"
        }), "\n", _jsx(_components.li, {
          children: "Third party ID"
        }), "\n", _jsx(_components.li, {
          children: "City ID"
        }), "\n", _jsx(_components.li, {
          children: "City name"
        }), "\n", _jsx(_components.li, {
          children: "Country code"
        }), "\n", _jsx(_components.li, {
          children: "Meta access token"
        }), "\n", _jsx(_components.li, {
          children: "Meta user ID"
        }), "\n", _jsx(_components.li, {
          children: "Instagram access token"
        }), "\n", _jsx(_components.li, {
          children: "Instagram username"
        }), "\n", _jsx(_components.li, {
          children: "Instagram user ID"
        }), "\n", _jsx(_components.li, {
          children: "Foursquare access token"
        }), "\n", _jsx(_components.li, {
          children: "Foursquare user ID"
        }), "\n", _jsx(_components.li, {
          children: "Extra data"
        }), "\n", _jsx(_components.li, {
          children: "Gender ID"
        }), "\n", _jsx(_components.li, {
          children: "Middle name"
        }), "\n", _jsx(_components.li, {
          children: "Mobile phone number"
        }), "\n", _jsx(_components.li, {
          children: "Receive email updates"
        }), "\n", _jsx(_components.li, {
          children: "Custom data"
        }), "\n", _jsx(_components.li, {
          children: "Tier override"
        }), "\n", _jsx(_components.li, {
          children: "Password generated"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Reference ", _jsx(_components.a, {
          href: "https://docs.oracle.com/en/cloud/saas/marketing/crowdtwist-develop/Developers/UserUpdate.html",
          children: "CrowdTwist's documentation"
        }), " for more detail on the types and requirements for each attribute to reduce the likelihood of rejected rows in your sync."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
